/**
 * Hala Control Center - Users & Permissions Management
 * Premium user and role management interface
 */

import { useState, useEffect } from 'react';
import { 
  Shield, 
  Plus,
  Search,
  Edit2,
  Trash2,
  Power,
  User,
  Mail,
  Key,
  CheckCircle,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/control-center`;

const COLORS = {
  primary: '#1B3A6B',
  gold: '#C5A572',
  success: '#1D8F4E',
  warning: '#F59E0B',
  error: '#EF4444',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  textLight: '#94A3B8',
};

const ROLES = {
  super_admin: { label: 'مدير عام', color: '#9333EA', description: 'صلاحيات كاملة على النظام' },
  operations_admin: { label: 'مدير العمليات', color: COLORS.primary, description: 'إدارة العمليات والجلسات' },
  catalog_admin: { label: 'مدير الكتالوج', color: COLORS.gold, description: 'إدارة الخدمات والبرامج' },
  session_reviewer: { label: 'مراجع الجلسات', color: COLORS.success, description: 'مراجعة الجلسات والمحادثات' },
  crm_agent: { label: 'موظف CRM', color: COLORS.warning, description: 'إدارة العملاء المحتملون' },
  reporting_analyst: { label: 'محلل التقارير', color: '#3B82F6', description: 'الوصول للتحليلات والتقارير' },
  service_owner: { label: 'مالك خدمة', color: '#EC4899', description: 'إدارة خدمات محددة' }
};

// User Form Modal
function UserFormModal({ user, isOpen, onClose, onSave }) {
  const [form, setForm] = useState(user || {
    username: '',
    email: '',
    password: '',
    full_name_ar: '',
    full_name_en: '',
    role: 'session_reviewer',
    status: 'active'
  });
  
  if (!isOpen) return null;
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };
  
  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    borderRadius: '8px',
    border: `1px solid ${COLORS.border}`,
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none'
  };
  
  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: COLORS.text,
    marginBottom: '8px'
  };
  
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }} onClick={onClose}>
      <div 
        style={{
          backgroundColor: COLORS.white,
          borderRadius: '16px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ 
          padding: '20px 24px', 
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: COLORS.text }}>
            {user ? 'تعديل المستخدم' : 'إضافة مستخدم جديد'}
          </h2>
          <button onClick={onClose} style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: COLORS.bg,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <X size={18} color={COLORS.textMuted} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} style={{ padding: '24px', overflowY: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>الاسم (عربي)</label>
              <input
                type="text"
                value={form.full_name_ar}
                onChange={e => setForm({ ...form, full_name_ar: e.target.value })}
                style={inputStyle}
                required
              />
            </div>
            <div>
              <label style={labelStyle}>Name (English)</label>
              <input
                type="text"
                value={form.full_name_en}
                onChange={e => setForm({ ...form, full_name_en: e.target.value })}
                style={inputStyle}
              />
            </div>
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>اسم المستخدم</label>
            <input
              type="text"
              value={form.username}
              onChange={e => setForm({ ...form, username: e.target.value })}
              style={inputStyle}
              required
            />
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>البريد الإلكتروني</label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              style={inputStyle}
              required
            />
          </div>
          
          {!user && (
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>كلمة المرور</label>
              <input
                type="password"
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                style={inputStyle}
                required={!user}
              />
            </div>
          )}
          
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>الدور</label>
            <select
              value={form.role}
              onChange={e => setForm({ ...form, role: e.target.value })}
              style={inputStyle}
            >
              {Object.entries(ROLES).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
          </div>
          
          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>الحالة</label>
            <select
              value={form.status}
              onChange={e => setForm({ ...form, status: e.target.value })}
              style={inputStyle}
            >
              <option value="active">نشط</option>
              <option value="inactive">معطل</option>
            </select>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: `1px solid ${COLORS.border}`,
                backgroundColor: COLORS.white,
                color: COLORS.text,
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              إلغاء
            </button>
            <button
              type="submit"
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: COLORS.primary,
                color: COLORS.white,
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              {user ? 'حفظ التعديلات' : 'إضافة المستخدم'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [roleFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      let url = `${API}/users`;
      if (roleFilter) url += `?role=${roleFilter}`;
      
      const res = await fetch(url);
      const data = await res.json();
      setUsers(data || []);
    } catch (e) {
      console.error('Failed to fetch users:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveUser = async (userData) => {
    try {
      const url = editingUser 
        ? `${API}/users/${editingUser.user_id}`
        : `${API}/users`;
      const method = editingUser ? 'PUT' : 'POST';
      
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      });
      
      setShowModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (e) {
      console.error('Failed to save user:', e);
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      await fetch(`${API}/users/${user.user_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          ...user, 
          status: user.status === 'active' ? 'inactive' : 'active' 
        })
      });
      fetchUsers();
    } catch (e) {
      console.error('Failed to toggle user status:', e);
    }
  };

  const filteredUsers = users.filter(user => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        user.full_name_ar?.toLowerCase().includes(query) ||
        user.full_name_en?.toLowerCase().includes(query) ||
        user.username?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const selectStyle = {
    padding: '10px 14px',
    borderRadius: '8px',
    border: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.white,
    fontSize: '13px',
    fontFamily: 'inherit',
    outline: 'none',
    cursor: 'pointer',
    minWidth: '160px'
  };

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: COLORS.text, margin: '0 0 8px' }}>المستخدمون والصلاحيات</h1>
        <p style={{ fontSize: '14px', color: COLORS.textMuted, margin: 0 }}>إدارة مستخدمي النظام وأدوارهم</p>
      </div>

      {/* Roles Overview */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
        gap: '16px',
        marginBottom: '24px'
      }}>
        {Object.entries(ROLES).slice(0, 4).map(([key, config]) => {
          const count = users.filter(u => u.role === key).length;
          return (
            <div key={key} style={{
              backgroundColor: COLORS.white,
              borderRadius: '12px',
              padding: '16px 20px',
              border: `1px solid ${COLORS.border}`,
              borderRight: `4px solid ${config.color}`
            }}>
              <p style={{ fontSize: '24px', fontWeight: 'bold', color: config.color, margin: '0 0 4px' }}>{count}</p>
              <p style={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, margin: '0 0 2px' }}>{config.label}</p>
              <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: 0 }}>{config.description}</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '20px',
        border: `1px solid ${COLORS.border}`,
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: COLORS.bg,
          padding: '10px 14px',
          borderRadius: '8px',
          flex: 1,
          minWidth: '250px'
        }}>
          <Search size={16} color={COLORS.textMuted} />
          <input
            type="text"
            placeholder="بحث بالاسم أو البريد الإلكتروني..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              border: 'none',
              backgroundColor: 'transparent',
              outline: 'none',
              flex: 1,
              fontSize: '13px',
              fontFamily: 'inherit'
            }}
          />
        </div>
        
        <select 
          value={roleFilter} 
          onChange={e => setRoleFilter(e.target.value)}
          style={selectStyle}
        >
          <option value="">كل الأدوار</option>
          {Object.entries(ROLES).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>

        <div style={{ marginRight: 'auto' }}>
          <button 
            onClick={() => { setEditingUser(null); setShowModal(true); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: COLORS.primary,
              color: COLORS.white,
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            <Plus size={16} />
            إضافة مستخدم
          </button>
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: COLORS.textMuted }}>
          جاري التحميل...
        </div>
      ) : filteredUsers.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px',
          backgroundColor: COLORS.white,
          borderRadius: '16px',
          border: `1px solid ${COLORS.border}`
        }}>
          <User size={48} color={COLORS.textLight} style={{ marginBottom: '16px' }} />
          <p style={{ color: COLORS.textMuted, margin: 0 }}>لا يوجد مستخدمون</p>
        </div>
      ) : (
        <div style={{
          backgroundColor: COLORS.white,
          borderRadius: '16px',
          border: `1px solid ${COLORS.border}`,
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: COLORS.bg }}>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>المستخدم</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>البريد الإلكتروني</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>الدور</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>الحالة</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted, width: '100px' }}>إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => {
                  const role = ROLES[user.role] || ROLES.session_reviewer;
                  
                  return (
                    <tr key={user.user_id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            backgroundColor: `${role.color}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <User size={18} color={role.color} />
                          </div>
                          <div>
                            <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: COLORS.text }}>{user.full_name_ar}</p>
                            <p style={{ margin: '2px 0 0', fontSize: '12px', color: COLORS.textMuted }}>@{user.username}</p>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{ fontSize: '13px', color: COLORS.textMuted }}>{user.email}</span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          backgroundColor: `${role.color}15`,
                          color: role.color,
                          fontSize: '12px',
                          fontWeight: 500
                        }}>
                          {role.label}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          backgroundColor: user.status === 'active' ? `${COLORS.success}15` : `${COLORS.textLight}15`,
                          color: user.status === 'active' ? COLORS.success : COLORS.textLight,
                          fontSize: '12px',
                          fontWeight: 500
                        }}>
                          <CheckCircle size={12} />
                          {user.status === 'active' ? 'نشط' : 'معطل'}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            onClick={() => { setEditingUser(user); setShowModal(true); }}
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '6px',
                              border: 'none',
                              backgroundColor: COLORS.bg,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Edit2 size={14} color={COLORS.textMuted} />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(user)}
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '6px',
                              border: 'none',
                              backgroundColor: user.status === 'active' ? `${COLORS.error}15` : `${COLORS.success}15`,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Power size={14} color={user.status === 'active' ? COLORS.error : COLORS.success} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Form Modal */}
      <UserFormModal
        user={editingUser}
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingUser(null); }}
        onSave={handleSaveUser}
      />
    </div>
  );
}
